import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Users, ChevronRight, ChevronLeft, Shuffle, ChevronUp, ChevronDown } from 'lucide-react';
import { useDraftContext } from '../context/DraftContext';
import { useLeague } from '../context/LeagueContext';
import { supabase } from '../supabase/supabase';
import TeamLogoImage from '../components/league/TeamLogoImage';
import BigBoardPanel from '../components/league/BigBoardPanel';
import DraftBoardGrid from '../components/league/DraftBoardGrid';
import TeamDrawer from '../components/league/TeamDrawer';
import { useBigBoard } from '../hooks/useBigBoard';
import { hapticSuccess } from '../capacitor/haptics';

// Draft Room is reached from a row inside the League tab, not a tab of its
// own, so it needs its own way back: the League tab's own section pills
// (Standings/Members/Rules) unmount along with the rest of that page once
// the user navigates here.
function BackToLeague({ leagueId, navigate }) {
  return (
    <div className="max-w-4xl mx-auto px-4 pt-4">
      <button
        onClick={() => navigate(`/${leagueId}/league`)}
        className="flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand transition-colors"
        style={{ minHeight: 44 }}
      >
        <ChevronLeft size={16} />
        League
      </button>
    </div>
  );
}

// Tracks who is currently in the draft room
function usePresence(leagueId, currentUserId) {
  const [onlineIds, setOnlineIds] = useState(new Set());

  useEffect(() => {
    if (!leagueId || !currentUserId) return;

    const channel = supabase.channel(`presence:draft:${leagueId}`, {
      config: { presence: { key: currentUserId } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        setOnlineIds(new Set(Object.keys(state)));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({ user_id: currentUserId, online_at: new Date().toISOString() });
        }
      });

    return () => { supabase.removeChannel(channel); };
  }, [leagueId, currentUserId]);

  return onlineIds;
}

// Counts up from a timestamp. No draft has a pick clock, but "how long have
// we been waiting on this pick" is still the thing everyone wants to know.
//
// Ticks every 30s, not every second. Picks take minutes here, so second
// precision is noise — and a 1s tick re-renders this whole page for all
// eight viewers, for hours, to animate a digit nobody reads.
function useElapsed(since) {
  const [seconds, setSeconds] = useState(null);

  useEffect(() => {
    if (!since) { setSeconds(null); return; }
    const start = new Date(since).getTime();
    if (Number.isNaN(start)) { setSeconds(null); return; }
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [since]);

  return seconds;
}

// Rounded to the minute past the first one, so the label never displays a
// stale seconds value between 30s ticks.
function formatElapsed(s) {
  if (s == null) return null;
  if (s < 60) return 'less than a minute';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export default function DraftRoom() {
  const { leagueId } = useParams();
  const { leagueData, isAdmin, members } = useLeague();
  const navigate = useNavigate();
  const {
    draft, picks, teams, availableTeams, pickedTeamIds,
    currentPickerUid, currentPickIndex, isMyTurn,
    canPickNow, canEnterPicks,
    totalPicks, nManagers, rosterByUser, memberMap, memberByMemberId,
    currentUserId, error,
    makePick, editPick, startDraft, saveDraftOrder, refresh,
  } = useDraftContext();

  const { board, limit: boardLimit, move: moveOnBoard, remove: removeFromBoard } = useBigBoard(leagueId, pickedTeamIds);

  const [search, setSearch]           = useState('');
  const [actionError, setActionError] = useState('');
  const [picking, setPicking]         = useState(false);
  const [starting, setStarting]       = useState(false);
  const [orderSaving, setOrderSaving] = useState(false);
  const [lobbyOrder, setLobbyOrder]   = useState([]);
  const [entryConference, setEntryConference] = useState('');
  const [entryTeamId, setEntryTeamId] = useState('');
  const [editingPick, setEditingPick] = useState(null);
  const [editTeamId, setEditTeamId]   = useState('');
  const [rosterUid, setRosterUid]     = useState(null);
  const [drawerTeam, setDrawerTeam]   = useState(null);
  const [showShortStart, setShowShortStart] = useState(false);
  const pickBoardRef = useRef(null);

  useEffect(() => {
    if (members.length === 0) return;
    const dbOrder = draft?.draft_order ?? [];
    if (dbOrder.length === members.length) {
      setLobbyOrder(dbOrder);
    } else {
      setLobbyOrder(members.map((m) => m.user_id));
    }
  }, [members.length, draft?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The roster panel opens on your own roster; the dropdown moves it.
  useEffect(() => {
    if (!rosterUid && currentUserId) setRosterUid(currentUserId);
  }, [currentUserId, rosterUid]);

  const persistOrder = useCallback(async (order) => {
    setOrderSaving(true);
    await saveDraftOrder(order);
    setOrderSaving(false);
  }, [saveDraftOrder]);

  const handleShuffle = () => {
    const shuffled = [...lobbyOrder].sort(() => Math.random() - 0.5);
    setLobbyOrder(shuffled);
    persistOrder(shuffled);
  };

  const handleMove = (index, dir) => {
    const newOrder = [...lobbyOrder];
    const swap = index + dir;
    if (swap < 0 || swap >= newOrder.length) return;
    [newOrder[index], newOrder[swap]] = [newOrder[swap], newOrder[index]];
    setLobbyOrder(newOrder);
    persistOrder(newOrder);
  };

  const onlineIds = usePresence(leagueId, currentUserId);

  // The current picker has been up since the previous pick landed, or since
  // the draft started, if this is pick 1. draft_picks timestamps its rows in
  // picked_at; there is no created_at column on that table.
  const onClockSince = picks.length > 0
    ? picks[picks.length - 1].picked_at
    : draft?.started_at;
  const onClockFor = useElapsed(draft?.status === 'active' ? onClockSince : null);

  const draftRounds = draft?.total_rounds ?? 7;

  // Who may enter a pick on someone else's behalf, named. "The commissioner"
  // alone is misleading once co-admins exist, and a manager who is going to
  // be away needs to know who to tell their pick to.
  const entrantNames = useMemo(() => {
    const ids = [leagueData?.admin_id, ...(leagueData?.co_admin_ids ?? [])].filter(Boolean);
    const names = ids
      .map((id) => memberMap[id]?.firstName || memberMap[id]?.name)
      .filter(Boolean);
    if (names.length === 0) return 'the commissioner';
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  }, [leagueData?.admin_id, leagueData?.co_admin_ids, memberMap]);

  const filteredTeams = availableTeams.filter((t) =>
    t.school.toLowerCase().includes(search.toLowerCase()) ||
    (t.conference ?? '').toLowerCase().includes(search.toLowerCase())
  );

  // The board has to move on its own for everyone who is watching rather
  // than picking, but it does not need a websocket to do it. Polling while
  // the tab is open and the draft is live covers draft night, and refetching
  // on focus covers a draft spread over days, where people open the app,
  // look, and close it again.
  //
  // 30s because an untimed draft moves in minutes. Whoever made the pick sees
  // it immediately via the refetch in makePick, so this interval only sets
  // how long everyone else waits.
  const draftIsActive = draft?.status === 'active';

  useEffect(() => {
    if (!draftIsActive) return;

    const tick = () => {
      if (document.visibilityState === 'visible') refresh({ quiet: true });
    };

    const id = setInterval(tick, 30000);
    window.addEventListener('focus', tick);
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(id);
      window.removeEventListener('focus', tick);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [draftIsActive, refresh]);

  const handlePick = async (teamId) => {
    if (!canPickNow || picking) return;
    setPicking(true);
    setActionError('');
    const result = await makePick(teamId);
    setPicking(false);
    if (result?.error) setActionError(result.error);
    else hapticSuccess();
  };

  // Entry on someone's behalf: conference narrows the team list so the admin
  // can find a team by where it plays rather than scrolling 130-odd names.
  const conferences = useMemo(
    () => [...new Set(teams.map((t) => t.conference).filter(Boolean))].sort(),
    [teams]
  );

  const entryTeams = useMemo(() => (
    entryConference
      ? availableTeams.filter((t) => t.conference === entryConference)
      : availableTeams
  ), [availableTeams, entryConference]);

  const handleEnterPick = async () => {
    if (!entryTeamId || picking) return;
    await handlePick(entryTeamId);
    setEntryTeamId('');
  };

  // Drafting from inside the drawer, so a manager can check the stats and
  // fixtures first. Only closes on success — an error stays visible behind
  // the drawer otherwise.
  const handleDraftFromDrawer = async (teamId) => {
    if (!canPickNow || picking) return;
    setPicking(true);
    setActionError('');
    const result = await makePick(teamId);
    setPicking(false);
    if (result?.error) {
      setActionError(result.error);
      return;
    }
    hapticSuccess();
    setDrawerTeam(null);
  };

  // Stable identity: DraftBoardGrid is memoized, and an inline arrow here
  // would give it a new prop on every render and defeat that.
  const handleEditPickClick = useCallback((pick) => {
    setEditingPick(pick);
    setEditTeamId(pick.team_id);
  }, []);

  const handleSaveEdit = async () => {
    if (!editingPick || !editTeamId) return;
    setPicking(true);
    setActionError('');
    const result = await editPick(editingPick.id, editTeamId);
    setPicking(false);
    if (result?.error) {
      setActionError(result.error);
      return;
    }
    setEditingPick(null);
    setEditTeamId('');
  };

  // Starting short resizes the league to whoever actually turned up. Without
  // it the league reads "6/8 managers" forever and every screen keeps
  // implying two more people are coming.
  const handleStartDraft = async (shrinkTo = null) => {
    setStarting(true);
    setActionError('');

    if (shrinkTo) {
      const { error: sizeError } = await supabase
        .from('leagues')
        .update({ max_managers: shrinkTo })
        .eq('id', leagueId);
      if (sizeError) {
        setStarting(false);
        setActionError(`Could not resize the league: ${sizeError.message}`);
        return;
      }
    }

    if (lobbyOrder.length > 0) await saveDraftOrder(lobbyOrder);
    const result = await startDraft();
    setStarting(false);
    setShowShortStart(false);
    if (result?.error) setActionError(result.error);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-surface-page flex items-center justify-center">
        <div className="text-danger-text text-lg">{error}</div>
      </div>
    );
  }

  // Team detail — same drawer the scouting page uses, so stats and fixtures
  // are identical in both places. In the draft room it also carries a Draft
  // button, but only when this user can actually pick right now.
  //
  // The active check is not redundant: canPickNow is true for an admin before
  // the draft starts, since they may always enter picks. Offering Draft in the
  // waiting room would send a make_pick that comes back "Draft is not active".
  const teamDrawer = drawerTeam && (
    <TeamDrawer
      teamName={drawerTeam}
      onClose={() => setDrawerTeam(null)}
      onDraft={draftIsActive && canPickNow ? handleDraftFromDrawer : null}
      draftBusy={picking}
    />
  );

  // Correcting a pick has to work from the live board and from the completed
  // board, so the modal is built once and rendered by both branches.
  const editModal = editingPick && (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-surface-card rounded-2xl p-5 w-full max-w-sm shadow-lg">
        <h3 className="font-bold text-lg text-ink mb-1">Edit pick #{editingPick.pick_number}</h3>
        <p className="text-sm text-ink-muted mb-4">
          {memberByMemberId[editingPick.member_id]?.name ?? '—'}
        </p>

        <select
          value={editTeamId}
          onChange={(e) => setEditTeamId(e.target.value)}
          className="w-full px-3 py-2.5 bg-surface-card border border-hairline rounded-xl text-sm text-ink mb-4 focus:outline-none focus:ring-2 focus:ring-brand"
        >
          {/* The pick's own team stays listed — it is not in availableTeams. */}
          <option value={editingPick.team_id}>
            {teams.find((t) => t.id === editingPick.team_id)?.school ?? editingPick.team_id} (current)
          </option>
          {availableTeams.map((t) => <option key={t.id} value={t.id}>{t.school}</option>)}
        </select>

        <div className="flex gap-2">
          <button
            onClick={() => { setEditingPick(null); setEditTeamId(''); }}
            className="flex-1 py-2.5 bg-surface-subtle hover:bg-surface-subtle rounded-xl font-semibold text-sm text-ink transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveEdit}
            disabled={picking || editTeamId === editingPick.team_id}
            className="flex-1 py-2.5 bg-brand hover:bg-brand disabled:opacity-40 rounded-xl font-bold text-sm text-white transition-colors"
          >
            {picking ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );

  const status = draft?.status ?? 'pending';

  // ─────────────────────────────────────────────────────────────────────────
  // PRE-DRAFT lobby
  // ─────────────────────────────────────────────────────────────────────────
  if (status === 'pending') {
    const allJoined = members.length >= (leagueData?.max_managers ?? 0);
    const waiting   = (leagueData?.max_managers ?? 0) - members.length;
    // Two is the smallest draft that means anything. Below that there is
    // nothing to snake between.
    const canStart  = members.length >= 2;

    return (
      <div className="min-h-screen bg-surface-page pb-24">
        <BackToLeague leagueId={leagueId} navigate={navigate} />

        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-black text-ink mb-2">Draft Room</h1>
            {/* Join progress lives in the Draft Order card below, which already
                counts members. This space explains how the draft will run,
                which is what nobody knows on arrival. There is nothing to
                count down to: the draft begins when the commissioner starts
                it. */}
          </div>

          {/* How the draft runs. Managers arrive here knowing nothing about
              the format, and the two things they most need to know are that
              nothing starts on its own and that no pick is on a clock. */}
          <div className="bg-surface-card rounded-2xl p-5 border border-hairline shadow-sm mb-6">
            <h2 className="font-bold text-ink mb-1">How the draft works</h2>
            <p className="text-sm text-ink-muted mb-4">
              {entrantNames} starts the draft once everyone is in. You pick for yourself
              when your turn comes round.
            </p>

            <ol className="space-y-2.5 text-sm text-ink">
              <li className="flex gap-2.5">
                <span className="font-bold text-brand flex-shrink-0">1</span>
                <span>
                  <strong>{draftRounds} rounds</strong>, so everyone finishes with {draftRounds} teams.
                  {' '}Order snakes, so whoever picks last in a round picks first in the next.
                </span>
              </li>

              <li className="flex gap-2.5">
                <span className="font-bold text-brand flex-shrink-0">2</span>
                <span>
                  <strong>There is no clock on a pick.</strong> Take the time you need. Everyone
                  can see how long the current pick has been running.
                </span>
              </li>

              <li className="flex gap-2.5">
                <span className="font-bold text-brand flex-shrink-0">3</span>
                <span>
                  If you are not available when your turn comes, <strong>{entrantNames}</strong>
                  {' '}can enter your pick for you. It is recorded as yours either way. That is
                  also how a league that drafts together in one room runs: one person enters
                  every pick.
                </span>
              </li>

              <li className="flex gap-2.5">
                <span className="font-bold text-brand flex-shrink-0">4</span>
                <span>
                  Everyone watches the same board update as picks land. Tap any team to see its
                  record, stats and upcoming fixtures before it goes. Use the big board below to
                  rank your targets in advance: only you can see it.
                </span>
              </li>
            </ol>

            <p className="text-xs text-ink-faint mt-4">
              {allJoined
                ? 'Everyone has joined. The commissioner can start when you are ready.'
                : `${members.length} of ${leagueData?.max_managers} managers have joined. Nobody is added automatically. The commissioner can wait for the rest, or start with who is here.`}
            </p>
          </div>

          {/* Waiting for a league to fill is the commonest way a draft never
              happens, so the admin can always start once there are two
              managers. Starting short resizes the league to match. */}
          {isAdmin && canStart && (
            <div className="mb-6">
              <button
                onClick={() => (allJoined ? handleStartDraft() : setShowShortStart(true))}
                disabled={starting}
                className="w-full py-4 bg-brand hover:bg-brand disabled:opacity-50 rounded-xl font-bold text-lg text-white transition-colors"
              >
                {starting
                  ? 'Starting…'
                  : allJoined
                  ? 'Start Draft'
                  : `Start Draft with ${members.length} managers`}
              </button>
              <p className="text-center text-ink-faint text-xs mt-2">
                The draft begins when you press this. It will not start on its own.
              </p>
            </div>
          )}

          {showShortStart && (
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
              <div className="bg-surface-card rounded-2xl p-5 w-full max-w-sm shadow-lg">
                <h3 className="font-bold text-lg text-ink mb-3">
                  Start with {members.length} managers?
                </h3>

                <p className="text-sm text-ink-muted mb-3">
                  {waiting} {waiting === 1 ? 'place is' : 'places are'} still empty. Starting now
                  sets your league size to {members.length}, and nobody else can join this season.
                </p>

                <p className="text-sm text-ink-muted mb-4">
                  Each manager still drafts {draftRounds} teams, so {draftRounds * members.length} of
                  the {teams.length} teams will be taken. This cannot be undone.
                </p>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowShortStart(false)}
                    disabled={starting}
                    className="flex-1 py-2.5 bg-surface-subtle hover:bg-surface-subtle rounded-xl font-semibold text-sm text-ink transition-colors"
                  >
                    Keep waiting
                  </button>
                  <button
                    onClick={() => handleStartDraft(members.length)}
                    disabled={starting}
                    className="flex-1 py-2.5 bg-brand hover:bg-brand disabled:opacity-40 rounded-xl font-bold text-sm text-white transition-colors"
                  >
                    {starting ? 'Starting…' : 'Start draft'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Draft order */}
          <div className="bg-surface-card rounded-2xl p-6 border border-hairline shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-brand" />
                <h2 className="text-base font-bold text-ink">
                  Draft Order ({members.length}/{leagueData?.max_managers})
                </h2>
                {orderSaving && <span className="text-xs text-ink-faint">Saving…</span>}
              </div>
              {isAdmin && (
                <button
                  onClick={handleShuffle}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-card border border-hairline hover:bg-surface-page text-ink rounded-lg text-sm font-semibold transition-colors"
                >
                  <Shuffle size={14} />
                  Shuffle
                </button>
              )}
            </div>

            <div className="space-y-2">
              {lobbyOrder.map((uid, i) => {
                const info     = memberMap[uid];
                const isOnline = onlineIds.has(uid);
                return (
                  <div key={uid} className="flex items-center gap-3 bg-surface-page rounded-xl p-3 border border-hairline">
                    <div className="w-8 h-8 bg-brand rounded-full flex items-center justify-center font-bold text-sm text-white flex-shrink-0">
                      {i + 1}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-ink flex items-center gap-2">
                        {info?.name ?? '—'}
                        <span
                          className="w-2 h-2 rounded-full flex-shrink-0 bg-surface-subtle"
                          style={isOnline ? { backgroundColor: 'var(--color-brand-green)' } : undefined}
                          title={isOnline ? 'In draft room' : 'Not in draft room'}
                        />
                      </div>
                      <div className="text-sm text-ink-muted">{info?.teamName}</div>
                    </div>

                    {uid === currentUserId && (
                      <span className="text-xs text-brand font-semibold mr-1">You</span>
                    )}

                    {isAdmin && (
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => handleMove(i, -1)}
                          disabled={i === 0}
                          className="p-0.5 hover:bg-surface-subtle rounded disabled:opacity-30 transition-colors text-ink-muted"
                        >
                          <ChevronUp size={14} />
                        </button>
                        <button
                          onClick={() => handleMove(i, 1)}
                          disabled={i === lobbyOrder.length - 1}
                          className="p-0.5 hover:bg-surface-subtle rounded disabled:opacity-30 transition-colors text-ink-muted"
                        >
                          <ChevronDown size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {isAdmin && (
              <p className="text-xs text-ink-faint mt-3 text-center">
                Round 1 picks in this order · Round 2 reverses (snake draft)
              </p>
            )}
          </div>

          {/* Big board */}
          <div className="mb-6">
            <BigBoardPanel
              board={board}
              teams={teams}
              limit={boardLimit}
              onSelect={setDrawerTeam}
              onMove={moveOnBoard}
              onRemove={removeFromBoard}
            />
          </div>

          {actionError && (
            <div className="bg-danger-bg border border-danger-border rounded-xl px-4 py-3 text-danger-text text-sm mb-4">
              {actionError}
            </div>
          )}
        </div>

        {teamDrawer}

      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIVE DRAFT
  // ─────────────────────────────────────────────────────────────────────────
  if (status === 'active') {
    const currentPicker = memberMap[currentPickerUid];
    const round         = Math.floor(currentPickIndex / (nManagers || 1)) + 1;
    const pickInRound   = (currentPickIndex % (nManagers || 1)) + 1;

    return (
      <div className="min-h-screen bg-surface-page pb-24">
        <BackToLeague leagueId={leagueId} navigate={navigate} />

        <div className="max-w-4xl mx-auto px-4 py-4">

          {/* Status bar */}
          <div className="bg-surface-card rounded-2xl p-4 border border-hairline shadow-sm mb-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-ink-muted text-sm">Round {round} · Pick {pickInRound}/{nManagers}</p>
                <p className="text-ink font-bold text-lg">
                  {isMyTurn
                    ? 'Your pick'
                    : `${currentPicker?.name ?? '—'} is on the clock`}
                </p>
              </div>

              {/* No deadline to count down to, so count up from the last pick
                  instead. Nobody is timed out; this is only so the room can
                  see who they are waiting on and for how long. */}
              {onClockFor !== null && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-surface-subtle text-ink-muted">
                  <Clock size={16} />
                  <span className="text-sm">on the clock for</span>
                  <span className="font-mono font-bold text-ink">{formatElapsed(onClockFor)}</span>
                </div>
              )}
            </div>

            {/* Draft order strip */}
            <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
              {draft.draft_order.map((uid) => {
                const info     = memberMap[uid];
                const active   = uid === currentPickerUid;
                const isOnline = onlineIds.has(uid);
                return (
                  <div
                    key={uid}
                    className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                      active
                        ? 'bg-brand text-white'
                        : uid === currentUserId
                        ? 'bg-brand-bg text-brand'
                        : 'bg-surface-subtle text-ink-faint'
                    }`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-surface-subtle"
                      style={isOnline ? { backgroundColor: 'var(--color-brand-green)' } : undefined}
                    />
                    {info?.firstName || info?.teamName || '?'}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Admin and co-admin entry. Available in every draft now, as the
              failsafe for a manager who is not around and as the way a league
              that drafts in one room gets its picks in. */}
          {canEnterPicks && (
            <div className="bg-surface-card rounded-2xl p-4 border border-brand-border shadow-sm mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-bold text-ink">
                  {isMyTurn
                    ? 'Enter your pick'
                    : `Pick for ${currentPicker?.teamName ?? currentPicker?.name ?? '—'}`}
                </h2>
                <span className="text-xs text-ink-faint">R{round} · P{pickInRound}</span>
              </div>

              {!isMyTurn && (
                <p className="text-sm text-ink-muted mb-3">
                  Use this if {currentPicker?.firstName || currentPicker?.name || 'this manager'} is
                  not available. The pick is recorded as theirs.
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  value={entryConference}
                  onChange={(e) => { setEntryConference(e.target.value); setEntryTeamId(''); }}
                  className="flex-1 min-w-0 px-3 py-2.5 bg-surface-card border border-hairline rounded-xl text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">All conferences</option>
                  {conferences.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>

                <select
                  value={entryTeamId}
                  onChange={(e) => setEntryTeamId(e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2.5 bg-surface-card border border-hairline rounded-xl text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  <option value="">Select team…</option>
                  {entryTeams.map((t) => <option key={t.id} value={t.id}>{t.school}</option>)}
                </select>

                <button
                  onClick={handleEnterPick}
                  disabled={!entryTeamId || picking}
                  className="px-6 py-2.5 bg-brand hover:bg-brand disabled:opacity-40 rounded-xl font-bold text-sm text-white transition-colors flex-shrink-0"
                >
                  {picking ? 'Saving…' : 'Enter'}
                </button>
              </div>
            </div>
          )}

          {actionError && (
            <div className="bg-danger-bg border border-danger-border rounded-xl px-4 py-3 text-danger-text text-sm mb-4">
              {actionError}
            </div>
          )}

          {/* Full draft board — the same grid everyone keeps for the season */}
          <div className="mb-4">
            <DraftBoardGrid
              draftOrder={draft.draft_order ?? []}
              picks={picks}
              teams={teams}
              memberMap={memberMap}
              memberByMemberId={memberByMemberId}
              totalRounds={draft.total_rounds ?? 7}
              currentPickIndex={currentPickIndex}
              currentUserId={currentUserId}
              canEnterPicks={canEnterPicks}
              onEditPick={handleEditPickClick}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

            {/* ── Pick board ── */}
            <div className="lg:col-span-2">
              <div className="bg-surface-card rounded-2xl p-4 border border-hairline shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold text-lg text-ink">Available Teams</h2>
                  <span className="text-ink-faint text-sm">{availableTeams.length} remaining</span>
                </div>

                <input
                  type="text"
                  placeholder="Search teams…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-2 bg-surface-card border border-hairline rounded-xl text-ink placeholder-ink-faint text-sm focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand mb-3"
                />

                <div ref={pickBoardRef} className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {filteredTeams.map((team) => (
                    <button
                      key={team.id}
                      onClick={() => setDrawerTeam(team.school)}
                      className="w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all duration-150 bg-surface-page border-hairline hover:bg-brand-bg hover:border-brand-border cursor-pointer"
                    >
                      <TeamLogoImage teamName={team.school} primaryColor={team.color} size={30} />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-ink truncate">{team.school}</div>
                        <div className="text-xs text-ink-faint">{team.conference}</div>
                      </div>
                      {team.currentSeason?.gamePoints != null && (
                        <div className="text-xs text-ink-faint flex-shrink-0">{team.currentSeason.gamePoints} pts</div>
                      )}
                      <ChevronRight size={14} className="text-ink-faint flex-shrink-0" />
                    </button>
                  ))}
                  {filteredTeams.length === 0 && (
                    <p className="text-center text-ink-faint py-6">No teams match your search</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Big board + pick log + rosters ── */}
            <div className="space-y-4">

              <BigBoardPanel
                board={board}
                teams={teams}
                limit={boardLimit}
                onSelect={setDrawerTeam}
                onMove={moveOnBoard}
                onRemove={removeFromBoard}
              />

              {/* Rosters — any manager's, chosen from the dropdown. Defaults
                  to your own, which is what it always showed before. */}
              <div className="bg-surface-card rounded-2xl p-4 border border-hairline shadow-sm">
                <select
                  value={rosterUid ?? ''}
                  onChange={(e) => setRosterUid(e.target.value)}
                  className="w-full px-3 py-2 mb-3 bg-surface-card border border-hairline rounded-xl text-sm font-bold text-ink focus:outline-none focus:ring-2 focus:ring-brand"
                >
                  {(draft.draft_order ?? []).map((uid) => {
                    const info = memberMap[uid];
                    const label = info?.teamName ?? info?.name ?? '—';
                    return (
                      <option key={uid} value={uid}>
                        {uid === currentUserId ? `${label} (You)` : label}
                        {info?.firstName ? ` · ${info.firstName}` : ''}
                      </option>
                    );
                  })}
                </select>

                {(rosterByUser[rosterUid] ?? []).length === 0 ? (
                  <p className="text-ink-faint text-sm">No picks yet</p>
                ) : (
                  <div className="space-y-1">
                    {(rosterByUser[rosterUid] ?? []).map((teamId) => {
                      const team = teams.find((t) => t.id === teamId);
                      return (
                        <div key={teamId} className="flex items-center gap-2 text-sm">
                          <TeamLogoImage teamName={team?.school} primaryColor={team?.color} size={18} />
                          <div className="min-w-0">
                            <div className="text-ink truncate">{team?.school ?? teamId}</div>
                            <div className="text-xs text-ink-faint truncate">{team?.conference}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {editModal}
        {teamDrawer}

      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // DRAFT COMPLETE
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-page pb-24">
      <BackToLeague leagueId={leagueId} navigate={navigate} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">🏆</div>
          <h1 className="text-3xl font-black text-ink mb-2">Draft Complete!</h1>
          <p className="text-ink-muted">All {totalPicks} picks have been made</p>
        </div>

        <button
          onClick={() => navigate(`/${leagueId}/lineup`)}
          className="w-full py-3 mb-8 bg-brand hover:bg-brand text-white rounded-xl font-bold text-lg transition-colors"
        >
          Go to My Lineup
        </button>

        {/* The board stays here for the rest of the season — this tab is the
            league's record of how the draft went. */}
        <div className="mb-8">
          <DraftBoardGrid
            draftOrder={draft?.draft_order ?? []}
            picks={picks}
            teams={teams}
            memberMap={memberMap}
            memberByMemberId={memberByMemberId}
            totalRounds={draft?.total_rounds ?? 7}
            currentUserId={currentUserId}
            canEnterPicks={canEnterPicks}
            onEditPick={handleEditPickClick}
          />
        </div>

        {/* Final rosters */}
        <div className="space-y-4">
          {members.map((m) => {
            const info   = memberMap[m.user_id];
            const roster = rosterByUser[m.user_id] ?? [];
            return (
              <div key={m.user_id} className="bg-surface-card rounded-2xl p-5 border border-hairline shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-bold text-ink">{info?.name}</div>
                    <div className="text-ink-muted text-sm">{info?.teamName}</div>
                  </div>
                  {m.user_id === currentUserId && (
                    <span className="text-xs text-brand font-semibold">You</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {roster.map((teamId) => {
                    const team = teams.find((t) => t.id === teamId);
                    return (
                      <div key={teamId} className="flex items-center gap-2 text-sm bg-surface-page rounded-lg p-2">
                        <TeamLogoImage teamName={team?.school} primaryColor={team?.color} size={18} />
                        <span className="truncate text-ink">{team?.school ?? teamId}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editModal}
      {teamDrawer}

    </div>
  );
}
